export function formatTimeBudget(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h 00m`;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

// Extract YYYY-MM-DD from either "2025-10-01" or "2025-10-01T00:00:00.000Z"
function normalizeDate(dateStr: string): string {
  return dateStr.split("T")[0];
}

export function formatDate(dateStr: string): string {
  const date = new Date(normalizeDate(dateStr) + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/** "Mon 14 Oct": weekday, day, month, for compact day pickers. */
export function formatShortDay(dateStr: string): string {
  const date = new Date(normalizeDate(dateStr) + "T00:00:00");
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  const month = date.toLocaleDateString("en-US", { month: "short" });
  return `${weekday} ${date.getDate()} ${month}`;
}

export function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(normalizeDate(startDate) + "T00:00:00");
  const end = new Date(normalizeDate(endDate) + "T00:00:00");
  const opts: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
  };
  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth()
  ) {
    return `${start.toLocaleDateString("en-US", { month: "long" })} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
  }
  return `${start.toLocaleDateString("en-US", opts)} - ${end.toLocaleDateString("en-US", opts)}`;
}

export function totalTimeBudget(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h total`;
  return `${h}h ${m}m total`;
}

export function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

export function minutesToTimeString(totalMinutes: number): string {
  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60) % 24;
  const m = wrapped % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

export function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)}m`;
  }
  return `${km.toFixed(1)}km`;
}

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago". */
export function formatRelativeTime(
  isoTimestamp: string,
  now: number = Date.now(),
): string {
  const elapsedMs = now - new Date(isoTimestamp).getTime();
  if (!Number.isFinite(elapsedMs)) return "at an unknown time";
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return "just now";
  const plural = (value: number, unit: string) =>
    `${value} ${unit}${value === 1 ? "" : "s"} ago`;
  if (minutes < 60) return plural(minutes, "minute");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return plural(hours, "hour");
  return plural(Math.floor(hours / 24), "day");
}
